if (!customElements.get('product-bundle-upsell')) {
  customElements.define(
    'product-bundle-upsell',
    class ProductBundleUpsell extends HTMLElement {
      constructor() {
        super();
        this.modal = null;
        this.historyLocked = false;
        this.closingViaUi = false;
        this.syncingCart = false;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.onModalClosed = this.onModalClosed.bind(this);
      }

      connectedCallback() {
        const modalSelector = this.querySelector('modal-opener')?.getAttribute('data-modal');
        if (modalSelector) {
          this.modal = document.querySelector(modalSelector);
        }

        const code = this.dataset.discountCode?.trim();
        if (code) {
          document.documentElement.dataset.bundleCheckoutDiscount = code;
        }

        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('popstate', this.onPopState);
        document.body.addEventListener('modalClosed', this.onModalClosed);

        if (this.modal) {
          this.modalObserver = new MutationObserver(() => {
            if (this.modal.hasAttribute('open')) {
              this.lockHistoryOnOpen();
              this.syncButtonsFromCart();
            }
          });
          this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'product-bundle-upsell') return;
          this.syncButtonsFromCart(event?.cartData);
        });

        window.addEventListener('pageshow', () => this.syncButtonsFromCart());
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') this.syncButtonsFromCart();
        });

        this.syncButtonsFromCart();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('popstate', this.onPopState);
        document.body.removeEventListener('modalClosed', this.onModalClosed);
        this.modalObserver?.disconnect();
        this.cartUpdateUnsubscriber?.();
      }

      getButtonTextEl(button) {
        return button.querySelector('.pbu-card__atc-text') || button;
      }

      setButtonLabel(button, text) {
        const el = this.getButtonTextEl(button);
        if (el) el.textContent = text;
      }

      isButtonLocked(button) {
        return button?.classList.contains('is-locked');
      }

      lockButtonSuccess(button, options = {}) {
        if (!button) return;
        const { animate = true } = options;
        const successLabel = button.dataset.successLabel || 'Added to cart!';
        this.setButtonLabel(button, successLabel);
        button.classList.remove('is-loading', 'is-animating', 'is-added');
        button.classList.add('is-success', 'is-locked');
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        delete button.dataset.pbuBusy;
        const qty = this.getCardQty(button);
        this.setCardQty(button, qty, true);
        if (animate) {
          button.classList.add('is-success-pop');
          window.setTimeout(() => button.classList.remove('is-success-pop'), 400);
        }
      }

      unlockButton(button) {
        if (!button || button.dataset.pbuBusy === 'true') return;
        button.classList.remove('is-success', 'is-locked', 'is-loading', 'is-animating', 'is-success-pop', 'is-added');
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        this.setButtonLabel(button, button.dataset.defaultLabel || 'Add to cart');
        this.setCardQty(button, Math.max(1, parseInt(button.dataset.pbuCartQty, 10) || 1), false);
      }

      getCardQty(button) {
        const input = button.closest('.pbu-card')?.querySelector('[data-pbu-qty-input]');
        const qty = parseInt(input?.value, 10);
        return Number.isFinite(qty) && qty > 0 ? Math.min(99, qty) : 1;
      }

      setCardQty(button, qty, disabled = false) {
        const card = button.closest('.pbu-card');
        const input = card?.querySelector('[data-pbu-qty-input]');
        if (!input) return;
        input.value = String(Math.max(1, qty || 1));
        input.disabled = disabled;
        card?.querySelectorAll('.pbu-qty__btn').forEach((el) => {
          el.disabled = disabled;
        });
      }

      onDocumentClick(event) {
        if (!this.modal) return;

        const minus = event.target.closest('[data-pbu-qty-minus]');
        const plus = event.target.closest('[data-pbu-qty-plus]');
        if ((minus || plus) && this.modal.contains(event.target)) {
          event.preventDefault();
          const wrap = event.target.closest('[data-pbu-qty]');
          const input = wrap?.querySelector('[data-pbu-qty-input]');
          if (!input || input.disabled) return;
          const current = Math.max(1, parseInt(input.value, 10) || 1);
          input.value = String(minus ? Math.max(1, current - 1) : Math.min(99, current + 1));
          return;
        }

        const btn = event.target.closest('[data-pbu-add]');
        if (!btn || !this.modal.contains(btn)) return;
        if (btn.disabled || this.isButtonLocked(btn)) return;
        if (btn.dataset.pbuBusy === 'true' || btn.classList.contains('is-loading')) return;
        event.preventDefault();
        this.addVariant(btn);
      }

      lockHistoryOnOpen() {
        if (!this.modal?.hasAttribute('open') || this.historyLocked) return;
        history.pushState({ pbuModal: this.modal.id }, '');
        this.historyLocked = true;
      }

      onPopState() {
        if (this.closingViaUi) {
          this.closingViaUi = false;
          return;
        }
        if (this.modal?.hasAttribute('open')) {
          this.historyLocked = false;
          this.modal.hide();
        }
      }

      onModalClosed() {
        if (!this.modal || this.modal.hasAttribute('open')) return;
        if (!this.historyLocked) return;
        this.historyLocked = false;
        this.closingViaUi = true;
        history.back();
      }

      addVariant(button) {
        const variantId = button.dataset.variantId;
        if (!variantId || button.dataset.pbuBusy === 'true') return;

        button.dataset.pbuBusy = 'true';
        button.classList.add('is-animating');
        window.requestAnimationFrame(() => {
          button.classList.remove('is-animating');
          button.classList.add('is-loading');
          this.setButtonLabel(button, button.dataset.loadingLabel || 'Adding...');
        });

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', String(this.getCardQty(button)));
        formData.append('properties[_add_source]', 'Cross-sell / Upsell');
        formData.append('sections', this.getSectionsToFetch().join(','));
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            delete button.dataset.pbuBusy;

            if (response.status) {
              button.classList.remove('is-loading');
              this.setButtonLabel(button, button.dataset.defaultLabel || 'Add to cart');
              this.announce(response.description || 'Could not add to cart');
              this.syncButtonsFromCart();
              return;
            }

            this.lockButtonSuccess(button, { animate: true });
            document.documentElement.dataset.bundleCheckoutDiscountUsed = 'true';
            this.applySections(response.sections);

            fetch(`${routes.cart_url}.js`)
              .then((r) => r.json())
              .then((cart) => {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-bundle-upsell',
                  productVariantId: variantId,
                  cartData: cart,
                });
                this.syncButtonsFromCart(cart);
              })
              .catch(() => {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-bundle-upsell',
                  productVariantId: variantId,
                });
                this.syncButtonsFromCart();
              });
          })
          .catch((e) => {
            delete button.dataset.pbuBusy;
            button.classList.remove('is-loading');
            this.setButtonLabel(button, button.dataset.defaultLabel || 'Add to cart');
            console.error(e);
            this.syncButtonsFromCart();
          });
      }

      getSectionsToFetch() {
        return ['cart-icon-bubble', 'cart-drawer'];
      }

      getButtons() {
        return this.modal ? Array.from(this.modal.querySelectorAll('[data-pbu-add]')) : [];
      }

      isFullCartPayload(cartPayload) {
        return (
          cartPayload &&
          Array.isArray(cartPayload.items) &&
          typeof cartPayload.item_count === 'number'
        );
      }

      syncButtonsFromCart(cartPayload) {
        if (this.syncingCart) return;
        this.syncingCart = true;

        const applyCart = (cart) => {
          const items = cart?.items || [];
          const qtyByVariant = new Map();
          items.forEach((item) => {
            const variantId = String(item.variant_id || item.id);
            qtyByVariant.set(variantId, (qtyByVariant.get(variantId) || 0) + (item.quantity || 0));
          });

          this.getButtons().forEach((btn) => {
            const id = String(btn.dataset.variantId);
            const qty = qtyByVariant.get(id) || 0;
            const inCart = qty > 0;
            if (inCart) {
              this.lockButtonSuccess(btn, { animate: false });
              this.setCardQty(btn, qty, true);
            } else {
              this.unlockButton(btn);
            }
          });
          this.updateCartStatus(cart);
          this.syncingCart = false;
        };

        if (this.isFullCartPayload(cartPayload)) {
          applyCart(cartPayload);
          return;
        }

        fetch(`${routes.cart_url}.js`)
          .then((r) => r.json())
          .then(applyCart)
          .catch(() => {
            this.syncingCart = false;
          });
      }

      updateCartStatus(cart) {
        const status = this.modal?.querySelector('[data-pbu-cart-status]');
        if (!status || !cart?.items) return;

        const popupVariantIds = new Set(this.getButtons().map((btn) => String(btn.dataset.variantId)));
        let hasPopupItem = false;

        cart.items.forEach((item) => {
          const variantId = String(item.variant_id || item.id);
          if (popupVariantIds.has(variantId) && (item.quantity || 0) > 0) {
            hasPopupItem = true;
          }
        });

        if (!hasPopupItem) {
          status.hidden = true;
          status.textContent = '';
          return;
        }

        status.hidden = false;
        status.textContent = 'Added items are in your cart — close to checkout when ready.';
      }

      applySections(sections) {
        if (!sections) return;

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.getSectionsToRender) {
          cartDrawer.getSectionsToRender().forEach((section) => {
            const sectionHtml = sections[section.id];
            if (!sectionHtml) return;
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement) return;
            sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(sectionHtml, section.selector);
          });
          cartDrawer.classList.remove('is-empty');
          const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
          overlay?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
        }

        const bubbleSection = document.getElementById('shopify-section-cart-icon-bubble');
        if (bubbleSection && sections['cart-icon-bubble']) {
          const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          const inner = html.querySelector('.shopify-section');
          if (inner) bubbleSection.innerHTML = inner.innerHTML;
        }

        cartDrawer?.setHeaderCartIconAccessibility?.();

        const countBadge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
        if (countBadge) {
          countBadge.classList.add('pbr-count-pop');
          setTimeout(() => countBadge.classList.remove('pbr-count-pop'), 450);
        }
      }

      announce(message) {
        const live = this.querySelector('[data-pbu-live]');
        if (live) live.textContent = message;
      }
    }
  );
}

function bundleUpsellCheckoutUrl() {
  const code = document.documentElement.dataset.bundleCheckoutDiscount?.trim();
  const used = document.documentElement.dataset.bundleCheckoutDiscountUsed === 'true';
  if (!code || !used) return '/checkout';
  return `/checkout?discount=${encodeURIComponent(code)}`;
}

window.bundleUpsellCheckoutUrl = bundleUpsellCheckoutUrl;
